import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "du-hindi-phd-interview-protest-2026",
  title: "Notice at Night, Interviews by Morning: DU's Hindi PhD Row Explained",
  description:
    "DU's Hindi Department could not hold its PhD interviews on 28 October after ABVP and DUSU protested an overnight notice. What happened, the history behind the row, and what applicants should do.",
  pubDate: "2026-10-28",
  updatedDate: "2026-10-28",
  authorSlug: "dhairya-madaan",
  category: "news",
  tags: ["DU news", "PhD admission", "DU Hindi Department", "NET", "JRF", "DUSU", "ABVP"],
  readMinutes: 7,
  heroEyebrow: "News · Research",
};

const src = (href: string, label: string) => (
  <a href={href} target="_blank" rel="noopener noreferrer">
    {label}
  </a>
);

export function Body() {
  return (
    <>
      <figure className="my-8">
        <img
          src="/blog/du-hindi-phd-interview-protest-2026.jpg"
          alt="Illustration of students with placards outside a university department building at dawn, next to a notice board and a clock."
          width={1200}
          height={688}
          fetchPriority="high"
          className="w-full h-auto border border-rule bg-card"
        />
        <figcaption className="mt-3 mono-label text-pencil text-center">
          Illustration. Not a photo of the actual protest.
        </figcaption>
      </figure>

      <div className="border border-rule bg-card p-5 my-6 text-sm">
        <strong className="mono-label text-grade">How we sourced this</strong>
        <p className="mt-2">
          The details of the 28 October protest come from{" "}
          {src("https://www.ndtv.com/education/notice-at-night-interviews-by-morning-delhi-university-hindi-phd-admissions-spark-row-12248312", "NDTV (28 October 2026)")}, with{" "}
          {src("https://www.bweducation.com/article/delhi-university-hindi-phd-interviews-cancelled-amid-student-protest-630940", "BW Education (28 October 2026)")}, and the demands are reported by{" "}
          {src("https://www.livehindustan.com/ncr/new-delhi/story-abvp-protests-alleged-irregularities-in-delhi-university-hindi-phd-admissions-201793193442321.html", "Hindustan's Delhi bureau (28 October 2026, in Hindi)")}
          . The background on the earlier April row comes from{" "}
          {src("https://www.tribuneindia.com/news/delhi/du-phd-admission-row-abvp-protests-exclusion-of-net-qualified-candidates/", "The Tribune (6 April 2026)")}
          . The situation is still developing. We do not predict outcomes, and nothing here is legal advice.
        </p>
      </div>

      <p>
        On the evening of 27 October, DU's Hindi Department put out a notification. It asked shortlisted PhD candidates to report at 9:15 the next morning. Less than a day later, the interviews had not happened at all, because ABVP and DUSU members were protesting outside the department.
      </p>
      <p>
        If you are a PhD applicant anywhere in DU, not just in Hindi, this row is worth following. It is about how little notice candidates get, who gets called for interviews, and what happens when students push back.
      </p>

      <h2>What happened on 27 and 28 October</h2>
      <p>
        The sequence, as reported by NDTV, is short and stark. On the evening of 27 October, the department notified shortlisted candidates to report at 9:15 am on 28 October. The first interview session was scheduled for 9:30 am with 45 candidates called. A second session at 2 pm listed 27 candidates.
      </p>
      <p>
        That gave candidates, many of them from outside Delhi, less than a day to arrange travel and prepare. By morning, ABVP and DUSU members had gathered outside the Hindi Department, and the interviews could not be conducted.
      </p>

      <h2>What the protesters are demanding</h2>
      <p>
        Two complaints sit at the centre of the protest. The first is the notice period itself. DUSU president Yash Dabas raised the time given to candidates to reach Delhi, according to NDTV. One student from Uttar Pradesh, who spoke to NDTV anonymously, said their interview was scheduled for 28 October and that an email to the university had gone unanswered.
      </p>
      <p>
        The second complaint is about who was called. Protesters allege that several eligible applicants did not get an adequate opportunity to appear, and they questioned the number of candidates shortlisted, noting that the department has more than 150 PhD seats. Hindustan's Delhi bureau reports ABVP's demands in the same direction: that all eligible candidates get a chance to interview, that the shortlist and schedule be released on time, that the process be made transparent, and that grievances be addressed. DUSU members said the protest would continue until the demands were met.
      </p>

      <h2>This row has a history</h2>
      <p>
        The 28 October protest is not an isolated event. In April 2026, ABVP protested at the same department over a notice that PhD admissions would be granted only to candidates who had qualified for the Junior Research Fellowship (JRF). According to The Tribune, candidates who had cleared the National Eligibility Test (NET) or were in the Only for PhD category were not considered, even though protesters said the department had around 125 vacant PhD seats and a list of 150 JRF-qualified candidates, while more than 500 NET-qualified and Only for PhD candidates were left out of the process.
      </p>
      <p>
        Student representatives argued at the time that excluding NET-qualified candidates went against UGC guidelines, under which both categories are eligible for doctoral admissions. And in July 2025, the same department saw a protest over delayed PhD results, which The Tribune covered with the phrase deliberate delays. A pattern of friction around this department's admission process is hard to miss.
      </p>

      <h3>Where the 30 percent rule fits in</h3>
      <p>
        Under the current DU PhD process, the entrance test carries 70 percent weight and the interview 30 percent, as we covered in our piece on the{" "}
        <a href="/blog/du-one-year-pg-pse-priority-high-court-2026" className="font-semibold underline text-grade">
          one-year PG case and the direct PhD route
        </a>
        . So the interview is not the whole decision, but it is a decisive slice of it. That is exactly why the notice period and the shortlist matter so much: a candidate who cannot reach Delhi, or who does not know they were shortlisted, loses their slice.
      </p>

      <h2>What applicants should take from this</h2>
      <h3>Watch the department page, not just your email</h3>
      <p>
        Shortlists and interview schedules in this case appear to have moved through department notifications rather than personal emails that arrived in time. If you have applied for a PhD anywhere in DU, check the department's page on the university site daily during admission season, and check the main notifications page too. An overnight notice gives you an overnight window.
      </p>
      <h3>Know your eligibility category</h3>
      <p>
        UGC's framework recognises multiple routes into a PhD: JRF, NET-qualified, the Only for PhD category, and the university's own entrance route. If you are eligible under any of these, keep the proof handy and be ready to point to it if you are left out of a shortlist. The April row turned on precisely this question.
      </p>
      <h3>Put complaints in writing, immediately</h3>
      <p>
        The student who emailed the university before the protest had the right instinct, even without a reply. Written complaints create a record. Grievance redressal, which ABVP is demanding, only works when there is a paper trail: the notification, your eligibility documents, your email, the date you sent it.
      </p>
      <h3>Prepare for a rescheduled round</h3>
      <p>
        Interviews that stall are usually rescheduled, not cancelled forever. If you were shortlisted for this round, keep your presentation and documents interview-ready, and watch for the department's next notification. Do not assume the original dates hold.
      </p>

      <h2>What to expect next</h2>
      <p>
        At the time of writing, DU has not announced a revised schedule for the Hindi Department's PhD interviews, and the department has not publicly responded to the demands in detail. The realistic outcomes are a fresh notification with new dates, a widened shortlist, or both. If you are affected, the practical move is to monitor the official notifications and keep your documents ready rather than relying on word of mouth.
      </p>

      <h2>The bottom line</h2>
      <p>
        A one-evening notice for a 9:15 am interview is not a schedule, it is a filter, and it filters hardest against candidates who live furthest away. Whether DU changes how it notifies PhD shortlists is now the test. We will update this article when the department announces revised dates.
      </p>
    </>
  );
}
